import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import { cn } from '@/lib/utils';

/* ─── Textarea ─────────────────────────────────────────────── */
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, ...props }, ref) => (
    <div className="space-y-1.5 w-full">
      {label && (
        <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-[var(--text-3)] mb-1">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        className={cn(
          'flex min-h-[100px] w-full rounded-xl border bg-[var(--bg-1)] px-4 py-3 text-[14px]',
          'border-[var(--border-c)] text-[var(--text-1)] placeholder:text-[var(--text-3)] shadow-sm',
          'outline-none transition-all duration-300 resize-none',
          'hover:border-[var(--brand)] hover:shadow-md',
          'focus:border-[var(--brand)] focus:ring-4 focus:ring-[var(--brand-dim)] focus:shadow-lg',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive focus:border-destructive focus:ring-red-500/10 focus:shadow-none',
          className
        )}
        {...props}
      />
      {error && <p className="text-[11px] font-medium text-destructive mt-1 ml-1 animate-in fade-in slide-in-from-top-1">{error}</p>}
    </div>
  )
);
Textarea.displayName = 'Textarea';

/* ─── Switch ──────────────────────────────────────────────── */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
      'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'data-[state=checked]:bg-[var(--brand)] data-[state=unchecked]:bg-[var(--bg-4)]',
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform',
        'data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0'
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

/* ─── Separator ───────────────────────────────────────────── */
const Separator = React.forwardRef<
  React.ElementRef<typeof SeparatorPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>
>(({ className, orientation = 'horizontal', decorative = true, ...props }, ref) => (
  <SeparatorPrimitive.Root
    ref={ref}
    decorative={decorative}
    orientation={orientation}
    className={cn(
      'shrink-0 bg-[var(--border-c)]',
      orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
      className
    )}
    {...props}
  />
));
Separator.displayName = SeparatorPrimitive.Root.displayName;

export { Textarea, Switch, Separator };
